/**
 * FotoDiaPage — Dashboard financiero consolidado por rango de fechas.
 *
 * Consume GET /turismo/finanzas/foto-dia y presenta:
 *  1. KPIs cabecera (6 chips grandes)
 *  2. VENTA: totales + split por canal, agencia, paquete
 *  3. RECAUDO: total, por cuenta destino, por origen, detalle
 *  4. CxC generadas: total, por agencia, detalle
 *  5. COMISIONES: generadas / anticipadas / pagadas / pendientes por agencia
 *  6. CAJA: neto = recaudo - comisiones pagadas
 *
 * El único filtro global es el rango de fechas. Cada bloque usa su propia
 * fecha semántica según el backend (venta y CxC → created_at de reserva;
 * recaudo → created_at del pago; comisiones pagadas → fecha_pago).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Chip, CircularProgress, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Tooltip, LinearProgress, Grid, Divider, Alert, Collapse,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return localStorage.getItem('empresa_activa') || u.company_id || u.empresa_id || '1';
  } catch { return '1'; }
}
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}
function fmtMoney(n) {
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n || 0));
}
function fmtMoneyCompact(n) {
  const num = Number(n || 0);
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (Math.abs(num) >= 1_000) return `$${Math.round(num / 1_000)}k`;
  return `$${num}`;
}
function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function pct(part, total) {
  const p = Number(total || 0) > 0 ? (Number(part || 0) / Number(total)) * 100 : 0;
  return p.toFixed(1) + '%';
}
function fmtDT(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

// KPI CARDS
const KPIS = [
  { key: 'pvp_bruto',        label: 'PVP bruto',       color: '#0891b2', icon: <TrendingUpIcon />,
    tooltip: 'Precio de lista antes de descuentos y comisiones' },
  { key: 'descuentos',       label: 'Descuentos',      color: '#f59e0b', icon: <LocalOfferIcon />,
    tooltip: 'Descuento comercial (Entretur) + descuento sobre comisión (agencia)' },
  { key: 'comision_efectiva', label: 'Comisiones',     color: '#7c3aed', icon: <AccountBalanceIcon />, expandable: 'comisiones',
    tooltip: 'Lo que las agencias ganan (comisión valor − descuento_comision_agencia). Click para desglose por agencia.' },
  { key: 'neto_entretur',    label: 'Neto Entretur',   color: '#0d9488', icon: <PriceCheckIcon />, primary: true, expandable: 'neto',
    tooltip: 'Lo que finalmente le queda a Entretur: precio_cliente − comisión efectiva. Click para ver reserva por reserva.' },
  { key: 'recaudo_total',    label: 'Recaudo caja',    color: '#16a34a', icon: <PaymentsIcon />, expandable: 'recaudo',
    tooltip: 'Plata que efectivamente entró a cuentas/bancos en el rango. Click para ver por cuenta, por origen y el detalle de movimientos.' },
  { key: 'cxc_generadas',    label: 'CxC generadas',   color: '#dc2626', icon: <AccountBalanceWalletIcon />,
    tooltip: 'Cuentas por cobrar a agencias generadas en el rango' },
  { key: 'cxp_comisiones',   label: 'CxP comisiones',  color: '#be185d', icon: <AccountBalanceIcon />,
    tooltip: 'Comisiones pendientes de pago a agencias (saldo vivo global hoy, no solo del rango)' },
];

const CANAL_LABEL = {
  whatsapp: 'WhatsApp / bot',
  web_agencia: 'Web (agencia)',
  web_directo: 'Web (directo)',
  telefono: 'Teléfono',
  cortesia: 'Cortesía',
};
const CANAL_COLOR = {
  whatsapp: '#25D366',
  web_agencia: '#8b5cf6',
  web_directo: '#0ea5e9',
  telefono: '#f59e0b',
  cortesia: '#94a3b8',
};

export default function FotoDiaPage() {
  const empresaId = getEmpresaId();
  const [fechaDesde, setFechaDesde] = useState(todayISO());
  const [fechaHasta, setFechaHasta] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedKpi, setExpandedKpi] = useState(null);  // 'comisiones' | 'recaudo' | 'cxc' | null

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_BASE}/turismo/finanzas/foto-dia?empresa_id=${empresaId}&fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`;
      const r = await fetch(url, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(String(e.message || e));
      setData(null);
    } finally { setLoading(false); }
  }, [empresaId, fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  // Presets rápidos
  const setHoy = () => { const t = todayISO(); setFechaDesde(t); setFechaHasta(t); };
  const setAyer = () => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    const iso = d.toISOString().slice(0, 10); setFechaDesde(iso); setFechaHasta(iso);
  };
  const setSemana = () => {
    const hasta = new Date();
    const desde = new Date(); desde.setDate(desde.getDate() - 6);
    setFechaDesde(desde.toISOString().slice(0, 10));
    setFechaHasta(hasta.toISOString().slice(0, 10));
  };
  const setMes = () => {
    const hoy = new Date();
    const desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    setFechaDesde(desde.toISOString().slice(0, 10));
    setFechaHasta(todayISO());
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2, bgcolor: '#0d948815',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0d9488',
        }}>
          <PhotoCameraIcon />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#134e4a' }}>
            Foto del día
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            Consolidado financiero por rango de fechas: venta, recaudo, CxC y comisiones.
          </Typography>
        </Box>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0',
          display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
          value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} sx={{ minWidth: 150 }} />
        <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
          value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} sx={{ minWidth: 150 }} />

        <Button size="small" variant="text" onClick={setHoy}>Hoy</Button>
        <Button size="small" variant="text" onClick={setAyer}>Ayer</Button>
        <Button size="small" variant="text" onClick={setSemana}>7 días</Button>
        <Button size="small" variant="text" onClick={setMes}>Mes actual</Button>

        <IconButton onClick={cargar} sx={{ bgcolor: '#f1f5f9' }}>
          <RefreshIcon />
        </IconButton>
        <Box sx={{ flex: 1 }} />
        {data?.rango && (
          <Chip label={data.rango.titulo} sx={{
            bgcolor: '#0d948815', color: '#0d9488', fontWeight: 700, fontSize: 12,
          }} />
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Error cargando: {error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress sx={{ color: '#0d9488' }} />
        </Box>
      )}

      {!loading && data && (
        <>
          <KPIsCabecera kpis={data.kpis} data={data}
            expandedKpi={expandedKpi} onToggle={setExpandedKpi} />
          <BloqueVenta venta={data.venta} />
          <BloqueRecaudo recaudo={data.recaudo} />
          <BloqueCxC cxc={data.cxc} />
          <BloqueComisiones comisiones={data.comisiones} />
          <BloqueCaja caja={data.caja} />
        </>
      )}
    </Box>
  );
}

// ============================================================================
// KPIs cabecera
// ============================================================================
function KPIsCabecera({ kpis, data, expandedKpi, onToggle }) {
  const handleClick = (k) => {
    if (!k.expandable) return;
    onToggle(expandedKpi === k.expandable ? null : k.expandable);
  };
  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={1.5}>
        {KPIS.map(k => {
          const isExpanded = k.expandable && expandedKpi === k.expandable;
          const clickable = !!k.expandable;
          return (
            <Grid item xs={6} sm={4} md={12 / 7} key={k.key}>
              <Tooltip title={k.tooltip || ''} arrow disableHoverListener={!k.tooltip}>
                <Paper
                  onClick={() => handleClick(k)}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={clickable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(k); }
                  } : undefined}
                  sx={{
                    p: 1.5, borderRadius: 2, height: '100%',
                    bgcolor: k.primary ? `${k.color}` : '#fff',
                    color: k.primary ? '#fff' : '#0f172a',
                    border: k.primary ? 'none' : `1px solid ${isExpanded ? k.color : `${k.color}30`}`,
                    borderLeft: k.primary ? 'none' : `3px solid ${k.color}`,
                    boxShadow: isExpanded ? `0 4px 12px ${k.color}30` : undefined,
                    display: 'flex', flexDirection: 'column', gap: 0.5,
                    cursor: clickable ? 'pointer' : (k.tooltip ? 'help' : 'default'),
                    transition: 'all 0.15s ease',
                    ...(clickable && {
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${k.color}40`,
                        borderColor: k.color,
                      },
                      '&:focus-visible': { outline: `2px solid ${k.color}`, outlineOffset: 2 },
                    }),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ color: k.primary ? '#fff' : k.color, display: 'flex', '& svg': { fontSize: 18 } }}>
                      {k.icon}
                    </Box>
                    <Typography sx={{
                      fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3,
                      color: k.primary ? '#f0fdfa' : '#64748b', textTransform: 'uppercase',
                      flex: 1,
                    }}>
                      {k.label}
                    </Typography>
                    {clickable && (
                      <ExpandMoreIcon sx={{
                        fontSize: 16, color: k.primary ? '#fff' : k.color,
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }} />
                    )}
                  </Box>
                  <Typography sx={{
                    fontSize: k.primary ? 22 : 18, fontWeight: 800, lineHeight: 1.1,
                    color: k.primary ? '#fff' : '#0f172a', mt: 0.25,
                  }}>
                    {fmtMoney(kpis[k.key])}
                  </Typography>
                </Paper>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {/* Panel expandible: aparece justo debajo del grid según qué KPI esté abierta */}
      <Collapse in={expandedKpi === 'comisiones'} timeout={200} unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          <PanelComisionesPorAgencia comisiones={data.comisiones} onClose={() => onToggle(null)} />
        </Box>
      </Collapse>
      <Collapse in={expandedKpi === 'neto'} timeout={200} unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          <PanelNetoEntretur venta={data.venta} onClose={() => onToggle(null)} />
        </Box>
      </Collapse>
      <Collapse in={expandedKpi === 'recaudo'} timeout={200} unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          <PanelRecaudo recaudo={data.recaudo} onClose={() => onToggle(null)} />
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Panel de detalle: comisiones desglosadas por agencia.
 * Se abre debajo del KPI "Comisiones" al hacer click. Reutiliza los mismos
 * datos del backend (data.comisiones), no requiere fetch adicional.
 */
function PanelComisionesPorAgencia({ comisiones, onClose }) {
  const c = comisiones;
  const [expandedAg, setExpandedAg] = useState(null);
  const toggleAg = (id) => setExpandedAg(prev => prev === id ? null : id);
  return (
    <Paper sx={{
      p: 2, borderRadius: 2, border: '1px solid #7c3aed40',
      borderTop: '3px solid #7c3aed',
      bgcolor: '#faf5ff',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <AccountBalanceIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#4c1d95', flex: 1 }}>
          Detalle de comisiones por agencia
        </Typography>
        <Chip size="small" label="Cerrar" onClick={onClose}
          sx={{ height: 22, fontSize: 11, cursor: 'pointer', bgcolor: '#fff' }} />
      </Box>

      {/* Métricas compactas — solo del rango. Todo cuadra: generada − anticipada − pagada = pendiente */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, pb: 1.5, alignItems: 'baseline',
          borderBottom: '1px dashed #c4b5fd' }}>
        <MetricMini label="Facturado" value={c.facturado_total} color="#0d9488"
          tooltip="Precio pagado por el cliente en las reservas del rango (sin descuentos)." />
        <MetricMini label="Comisión generada" value={c.generadas} color="#7c3aed"
          tooltip="Lo que corresponde a las agencias por estas reservas." />
        <Typography sx={{ fontSize: 18, color: '#94a3b8', fontWeight: 300 }}>−</Typography>
        <MetricMini label="Anticipadas" value={c.anticipadas} color="#0ea5e9"
          tooltip="Comisiones que la agencia se retuvo al cobrar al cliente. No entraron a caja de Entretur." />
        {c.pagadas > 0 && (
          <>
            <Typography sx={{ fontSize: 18, color: '#94a3b8', fontWeight: 300 }}>−</Typography>
            <MetricMini label="Pagadas" value={c.pagadas} color="#16a34a"
              tooltip="Liquidaciones ya realizadas para estas reservas." />
          </>
        )}
        <Typography sx={{ fontSize: 18, color: '#94a3b8', fontWeight: 300 }}>=</Typography>
        <MetricMini label="Pendiente por pagar" value={c.pendientes_rango} color="#dc2626" bold
          tooltip="Lo que Entretur aún debe a las agencias por las reservas del rango." />
      </Box>

      {c.por_agencia.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: '#94a3b8', fontStyle: 'italic' }}>
          Sin comisiones generadas en el rango.
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...cellHead, width: 24 }} />
                <TableCell sx={cellHead}>Agencia</TableCell>
                <TableCell sx={cellHead} align="right">Res.</TableCell>
                <TableCell sx={cellHead} align="right">Facturado</TableCell>
                <TableCell sx={cellHead} align="right">Comisión</TableCell>
                <TableCell sx={cellHead} align="right">Anticipada</TableCell>
                {c.pagadas > 0 && (
                  <TableCell sx={cellHead} align="right">Pagada</TableCell>
                )}
                <TableCell sx={cellHead} align="right">Pendiente</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {c.por_agencia.map(a => {
                const isOpen = expandedAg === a.agencia_id;
                const colCount = c.pagadas > 0 ? 8 : 7;
                return (
                  <React.Fragment key={a.agencia_id}>
                    <TableRow hover onClick={() => toggleAg(a.agencia_id)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: isOpen ? '#f3e8ff' : undefined,
                        '&:hover': { bgcolor: isOpen ? '#f3e8ff' : '#faf5ff' },
                      }}>
                      <TableCell sx={{ ...cell, width: 24, py: 0.25 }}>
                        <KeyboardArrowRightIcon sx={{
                          fontSize: 18, color: '#7c3aed',
                          transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }} />
                      </TableCell>
                      <TableCell sx={cell}>{a.agencia_nombre}</TableCell>
                      <TableCell sx={cell} align="right">{a.num_reservas}</TableCell>
                      <TableCell sx={cell} align="right">{fmtMoney(a.facturado)}</TableCell>
                      <TableCell sx={cell} align="right">{fmtMoney(a.generada)}</TableCell>
                      <TableCell sx={cell} align="right"
                        style={{ color: a.anticipada > 0 ? '#0ea5e9' : '#94a3b8' }}>
                        {fmtMoney(a.anticipada)}
                      </TableCell>
                      {c.pagadas > 0 && (
                        <TableCell sx={cell} align="right"
                          style={{ color: a.pagada > 0 ? '#16a34a' : '#94a3b8' }}>
                          {fmtMoney(a.pagada)}
                        </TableCell>
                      )}
                      <TableCell sx={cellStrong} align="right"
                        style={{ color: a.pendiente > 0 ? '#dc2626' : '#94a3b8' }}>
                        {fmtMoney(a.pendiente)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ p: 0, border: 0 }} colSpan={colCount}>
                        <Collapse in={isOpen} timeout={180} unmountOnExit>
                          <SubTablaReservasAgencia reservas={a.reservas || []}
                            mostrarPagada={c.pagadas > 0} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

/**
 * Sub-tabla anidada con las reservas específicas que una agencia vendió
 * en el rango. Aparece cuando el usuario expande una fila del panel.
 */
function SubTablaReservasAgencia({ reservas, mostrarPagada }) {
  return (
    <Box sx={{ pl: 5, pr: 2, py: 1.5, bgcolor: '#fdfaff',
        borderLeft: '2px solid #c4b5fd', borderTop: '1px dashed #e2e8f0' }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#7c3aed',
          textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75 }}>
        {reservas.length} reserva{reservas.length !== 1 ? 's' : ''} en el rango
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={subCellHead}>Reserva</TableCell>
            <TableCell sx={subCellHead}>Cliente</TableCell>
            <TableCell sx={subCellHead}>Pasadía</TableCell>
            <TableCell sx={subCellHead}>Fecha</TableCell>
            <TableCell sx={subCellHead} align="center">Pax</TableCell>
            <TableCell sx={subCellHead}>Estado</TableCell>
            <TableCell sx={subCellHead} align="right">Facturado</TableCell>
            <TableCell sx={subCellHead} align="right">Comisión</TableCell>
            <TableCell sx={subCellHead} align="right">Anticipada</TableCell>
            {mostrarPagada && <TableCell sx={subCellHead} align="right">Pagada</TableCell>}
            <TableCell sx={subCellHead} align="right">Pendiente</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reservas.map(r => (
            <TableRow key={r.reservacion_id} hover>
              <TableCell sx={subCell}>
                <span style={{ fontFamily: 'monospace', color: '#0891b2', fontWeight: 600 }}>
                  {r.codigo_reserva}
                </span>
              </TableCell>
              <TableCell sx={subCell}>{r.nombre_cliente}</TableCell>
              <TableCell sx={subCell}>
                {r.paquete_nombre || <span style={{ color: '#cbd5e1' }}>—</span>}
              </TableCell>
              <TableCell sx={subCell}>
                {r.fecha_servicio ? new Date(r.fecha_servicio.slice(0, 10) + 'T00:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }) : '—'}
              </TableCell>
              <TableCell sx={subCell} align="center">
                {r.pax}
                {(r.ninos > 0 || r.bebes > 0) && (
                  <span style={{ color: '#94a3b8', fontSize: 10 }}>
                    {' '}({r.adultos}A{r.ninos > 0 ? `·${r.ninos}N` : ''}{r.bebes > 0 ? `·${r.bebes}B` : ''})
                  </span>
                )}
              </TableCell>
              <TableCell sx={subCell}>
                <Chip size="small" label={r.estado}
                  sx={{ height: 18, fontSize: 9.5, bgcolor: '#f1f5f9' }} />
              </TableCell>
              <TableCell sx={subCell} align="right">{fmtMoney(r.facturado)}</TableCell>
              <TableCell sx={subCell} align="right">{fmtMoney(r.generada)}</TableCell>
              <TableCell sx={subCell} align="right"
                style={{ color: r.anticipada > 0 ? '#0ea5e9' : '#cbd5e1' }}>
                {fmtMoney(r.anticipada)}
              </TableCell>
              {mostrarPagada && (
                <TableCell sx={subCell} align="right"
                  style={{ color: r.pagada > 0 ? '#16a34a' : '#cbd5e1' }}>
                  {fmtMoney(r.pagada)}
                </TableCell>
              )}
              <TableCell sx={{ ...subCell, fontWeight: 700 }} align="right"
                style={{ color: r.pendiente > 0 ? '#dc2626' : '#94a3b8' }}>
                {fmtMoney(r.pendiente)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

const subCellHead = { fontSize: 9.5, fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: 0.3, py: 0.5, borderBottom: '1px solid #e2e8f0' };
const subCell = { fontSize: 11.5, color: '#334155', py: 0.5 };


/**
 * Panel de detalle: Neto Entretur desglosado reserva por reserva.
 * Muestra la ecuación (PVP → descuentos → cliente pagó → comisión → neto),
 * split por canal y tabla completa ordenada por neto descendente.
 */
function PanelNetoEntretur({ venta, onClose }) {
  const t = venta.totales;
  const reservas = venta.reservas || [];
  return (
    <Paper sx={{
      p: 2, borderRadius: 2, border: '1px solid #0d948840',
      borderTop: '3px solid #0d9488',
      bgcolor: '#f0fdfa',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <PriceCheckIcon sx={{ color: '#0d9488', fontSize: 20 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#134e4a', flex: 1 }}>
          Detalle del Neto Entretur
        </Typography>
        <Chip size="small" label="Cerrar" onClick={onClose}
          sx={{ height: 22, fontSize: 11, cursor: 'pointer', bgcolor: '#fff' }} />
      </Box>

      {/* Ecuación visual: PVP → descuentos → cliente pagó → comisión = NETO */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, pb: 1.5,
          alignItems: 'baseline', borderBottom: '1px dashed #99f6e4' }}>
        <MetricMini label="PVP bruto" value={t.pvp_bruto} color="#0891b2"
          tooltip="Precio de lista antes de descuentos" />
        {t.descuento_comercial > 0 && (
          <>
            <OperadorEcuacion op="−" />
            <MetricMini label="Desc. comercial" value={t.descuento_comercial} color="#f59e0b"
              tooltip="Descuento otorgado por Entretur al cliente" />
          </>
        )}
        {t.descuento_comision > 0 && (
          <>
            <OperadorEcuacion op="−" />
            <MetricMini label="Desc. s/ comisión" value={t.descuento_comision} color="#f59e0b"
              tooltip="Descuento que la agencia rebajó al cliente" />
          </>
        )}
        <OperadorEcuacion op="=" />
        <MetricMini label="Cliente pagó" value={t.precio_cliente} color="#334155" />
        <OperadorEcuacion op="−" />
        <MetricMini label="Comisión efectiva" value={t.comision_efectiva} color="#7c3aed"
          tooltip="Lo que se lleva la agencia" />
        <OperadorEcuacion op="=" />
        <MetricMini label="Neto Entretur" value={t.neto_entretur} color="#0d9488" bold />
      </Box>

      {/* Split por canal — barras horizontales rápidas */}
      {venta.por_canal.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#0d9488',
              textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75 }}>
            Neto Entretur por canal
          </Typography>
          {venta.por_canal.map(c => (
            <BarRow key={c.canal}
              label={CANAL_LABEL[c.canal] || c.canal}
              value={fmtMoney(c.neto_entretur)}
              valueRight={`${c.num} res · ${c.pax} pax`}
              pct={t.neto_entretur > 0 ? (c.neto_entretur / t.neto_entretur) * 100 : 0}
              color={CANAL_COLOR[c.canal] || '#0d9488'} />
          ))}
        </Box>
      )}

      {/* Tabla de todas las reservas */}
      {reservas.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: '#94a3b8', fontStyle: 'italic' }}>
          Sin reservas en el rango.
        </Typography>
      ) : (
        <Box>
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#0d9488',
              textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75 }}>
            {reservas.length} reserva{reservas.length !== 1 ? 's' : ''} · ordenadas por neto Entretur
          </Typography>
          <TableContainer sx={{ maxHeight: 480, bgcolor: '#fff', borderRadius: 1,
              border: '1px solid #ccfbf1' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={cellHead}>Reserva</TableCell>
                  <TableCell sx={cellHead}>Cliente</TableCell>
                  <TableCell sx={cellHead}>Pasadía</TableCell>
                  <TableCell sx={cellHead}>Fecha</TableCell>
                  <TableCell sx={cellHead} align="center">Pax</TableCell>
                  <TableCell sx={cellHead}>Canal / Agencia</TableCell>
                  <TableCell sx={cellHead} align="right">Cliente pagó</TableCell>
                  <TableCell sx={cellHead} align="right">Comisión</TableCell>
                  <TableCell sx={cellHead} align="right">Neto Entretur</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservas.map(r => (
                  <TableRow key={r.reservacion_id} hover>
                    <TableCell sx={cellSmall}>
                      <span style={{ fontFamily: 'monospace', color: '#0891b2', fontWeight: 600 }}>
                        {r.codigo_reserva}
                      </span>
                    </TableCell>
                    <TableCell sx={cellSmall}>{r.nombre_cliente}</TableCell>
                    <TableCell sx={cellSmall}>
                      {r.paquete_nombre || <span style={{ color: '#cbd5e1' }}>—</span>}
                    </TableCell>
                    <TableCell sx={cellSmall}>
                      {r.fecha_servicio
                        ? new Date(r.fecha_servicio.slice(0, 10) + 'T00:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })
                        : '—'}
                    </TableCell>
                    <TableCell sx={cellSmall} align="center">
                      {r.pax}
                      {(r.ninos > 0 || r.bebes > 0) && (
                        <span style={{ color: '#94a3b8', fontSize: 10 }}>
                          {' '}({r.adultos}A{r.ninos > 0 ? `·${r.ninos}N` : ''}{r.bebes > 0 ? `·${r.bebes}B` : ''})
                        </span>
                      )}
                    </TableCell>
                    <TableCell sx={cellSmall}>
                      {r.agencia_nombre ? (
                        <span style={{ color: '#7c3aed', fontWeight: 500 }}>{r.agencia_nombre}</span>
                      ) : (
                        <Chip size="small" label={CANAL_LABEL[r.canal] || r.canal}
                          sx={{ height: 18, fontSize: 10,
                            bgcolor: `${CANAL_COLOR[r.canal] || '#0d9488'}15`,
                            color: CANAL_COLOR[r.canal] || '#0d9488' }} />
                      )}
                    </TableCell>
                    <TableCell sx={cellSmall} align="right">{fmtMoney(r.precio_cliente)}</TableCell>
                    <TableCell sx={cellSmall} align="right"
                      style={{ color: r.comision_efectiva > 0 ? '#7c3aed' : '#cbd5e1' }}>
                      {fmtMoney(r.comision_efectiva)}
                    </TableCell>
                    <TableCell sx={cellStrong} align="right" style={{ color: '#0d9488' }}>
                      {fmtMoney(r.neto_entretur)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Paper>
  );
}

function OperadorEcuacion({ op }) {
  return (
    <Typography sx={{ fontSize: 20, color: '#94a3b8', fontWeight: 300, alignSelf: 'center' }}>
      {op}
    </Typography>
  );
}


/**
 * Panel de detalle: Recaudo desglosado por cuenta destino, por origen
 * y con listado completo de movimientos del rango.
 */
function PanelRecaudo({ recaudo, onClose }) {
  return (
    <Paper sx={{
      p: 2, borderRadius: 2, border: '1px solid #16a34a40',
      borderTop: '3px solid #16a34a',
      bgcolor: '#f0fdf4',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <PaymentsIcon sx={{ color: '#16a34a', fontSize: 20 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#14532d', flex: 1 }}>
          Detalle del recaudo
        </Typography>
        <Chip size="small" label="Cerrar" onClick={onClose}
          sx={{ height: 22, fontSize: 11, cursor: 'pointer', bgcolor: '#fff' }} />
      </Box>

      {/* Métricas rápidas */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2, pb: 1.5,
          borderBottom: '1px dashed #bbf7d0', alignItems: 'baseline' }}>
        <MetricMini label="Total recaudado" value={recaudo.total} color="#16a34a" bold />
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
              color: '#64748b', textTransform: 'uppercase' }}>
            Movimientos
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0f172a', mt: 0.15 }}>
            {recaudo.num_movimientos}
          </Typography>
        </Box>
      </Box>

      {recaudo.num_movimientos === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: '#94a3b8', fontStyle: 'italic' }}>
          Sin movimientos de recaudo en el rango.
        </Typography>
      ) : (
        <>
          {/* Splits en 2 columnas */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 1,
                  border: '1px solid #dcfce7' }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#16a34a',
                    textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75 }}>
                  Por cuenta destino
                </Typography>
                {recaudo.por_cuenta.map((c, i) => (
                  <BarRow key={i}
                    label={c.cuenta_nombre}
                    sublabel={c.tipo || (c.cuenta_id === null ? 'plata que entró pero al pago no se le asignó cuenta' : null)}
                    value={fmtMoney(c.monto)} valueRight={`${c.num} mov.`}
                    pct={recaudo.total > 0 ? (c.monto / recaudo.total) * 100 : 0}
                    color={c.cuenta_id === null ? '#f59e0b' : '#16a34a'} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ bgcolor: '#fff', p: 1.5, borderRadius: 1,
                  border: '1px solid #dcfce7' }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#16a34a',
                    textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75 }}>
                  Por origen
                </Typography>
                {recaudo.por_origen.map(o => (
                  <BarRow key={o.origen}
                    label={o.label}
                    value={fmtMoney(o.monto)} valueRight={`${o.num} mov.`}
                    pct={recaudo.total > 0 ? (o.monto / recaudo.total) * 100 : 0}
                    color="#0d9488" />
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Detalle */}
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#16a34a',
              textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75 }}>
            {recaudo.detalle.length} movimiento{recaudo.detalle.length !== 1 ? 's' : ''} · más reciente primero
          </Typography>
          <TableContainer sx={{ maxHeight: 480, bgcolor: '#fff', borderRadius: 1,
              border: '1px solid #dcfce7' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={cellHead}>Fecha</TableCell>
                  <TableCell sx={cellHead}>Reserva</TableCell>
                  <TableCell sx={cellHead}>Cliente</TableCell>
                  <TableCell sx={cellHead}>Agencia</TableCell>
                  <TableCell sx={cellHead}>Origen</TableCell>
                  <TableCell sx={cellHead}>Cuenta</TableCell>
                  <TableCell sx={cellHead} align="right">Monto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recaudo.detalle.map(p => (
                  <TableRow key={p.pago_id} hover>
                    <TableCell sx={cellSmall}>{fmtDT(p.fecha)}</TableCell>
                    <TableCell sx={cellSmall}>
                      <span style={{ fontFamily: 'monospace', color: '#0891b2', fontWeight: 600 }}>
                        {p.codigo_reserva || '—'}
                      </span>
                    </TableCell>
                    <TableCell sx={cellSmall}>{p.nombre_cliente || '—'}</TableCell>
                    <TableCell sx={cellSmall}>
                      {p.agencia_nombre || <span style={{ color: '#cbd5e1' }}>—</span>}
                    </TableCell>
                    <TableCell sx={cellSmall}>
                      <Chip size="small" label={p.origen_label}
                        sx={{ height: 20, fontSize: 10, bgcolor: '#f1f5f9' }} />
                    </TableCell>
                    <TableCell sx={cellSmall}>
                      {p.cuenta_nombre || <span style={{ color: '#cbd5e1', fontSize: 11 }}>sin cuenta</span>}
                    </TableCell>
                    <TableCell sx={cellStrong} align="right" style={{ color: '#16a34a' }}>
                      {fmtMoney(p.monto)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
}

function MetricMini({ label, value, color, bold, tooltip }) {
  const body = (
    <Box sx={{ minWidth: 140 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
          color: '#64748b', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: bold ? 17 : 15, fontWeight: 800, color, mt: 0.15,
          cursor: tooltip ? 'help' : 'default' }}>
        {fmtMoney(value)}
      </Typography>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip} arrow>{body}</Tooltip> : body;
}

// ============================================================================
// Cabecera de sección reusable
// ============================================================================
function SectionHeader({ num, title, subtitle, color, icon }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25, mt: 3 }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1.5, bgcolor: `${color}15`,
        color, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: 14,
      }}>
        {num}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {icon}
    </Box>
  );
}

// ============================================================================
// Bloque 1 · VENTA
// ============================================================================
function BloqueVenta({ venta }) {
  const t = venta.totales;
  return (
    <Box>
      <SectionHeader num="1" title="Venta" color="#0891b2"
        subtitle="Reservas creadas en el rango — camino desde PVP hasta lo que Entretur conserva" />
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
            <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 1 }}>COMPOSICIÓN</Typography>
            <Row label="Reservas" value={t.num_reservas} />
            <Row label="Pax" value={t.num_pax} />
            {t.num_pax_cortesia > 0 && <Row label="Pax cortesía" value={t.num_pax_cortesia} muted />}
            <Divider sx={{ my: 1 }} />
            <Row label="PVP bruto" value={fmtMoney(t.pvp_bruto)} />
            <Row label="− Desc. comercial" value={fmtMoney(t.descuento_comercial)} muted />
            <Row label="− Desc. sobre comisión" value={fmtMoney(t.descuento_comision)} muted />
            <Row label="Cliente pagó" value={fmtMoney(t.precio_cliente)} />
            <Row label="− Comisión efectiva" value={fmtMoney(t.comision_efectiva)} muted />
            <Divider sx={{ my: 1 }} />
            <Row label="NETO ENTRETUR" value={fmtMoney(t.neto_entretur)} bold />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
            <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 1 }}>
              POR CANAL <span style={{ fontWeight: 400 }}>(neto Entretur)</span>
            </Typography>
            {venta.por_canal.length === 0 && <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Sin datos</Typography>}
            {venta.por_canal.map(c => (
              <BarRow key={c.canal}
                label={CANAL_LABEL[c.canal] || c.canal}
                value={fmtMoney(c.neto_entretur)} valueRight={`${c.num} · ${c.pax} pax`}
                pct={t.neto_entretur > 0 ? (c.neto_entretur / t.neto_entretur) * 100 : 0}
                color={CANAL_COLOR[c.canal] || '#0891b2'} />
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
            <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 1 }}>
              TOP AGENCIAS <span style={{ fontWeight: 400 }}>(neto Entretur)</span>
            </Typography>
            {venta.por_agencia.length === 0 && <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Sin ventas de agencias</Typography>}
            {venta.por_agencia.slice(0, 6).map(a => (
              <BarRow key={a.agencia_id}
                label={a.agencia_nombre}
                sublabel={`Comisión ${fmtMoney(a.comision_efectiva)}`}
                value={fmtMoney(a.neto_entretur)} valueRight={`${a.num_reservas} · ${a.pax} pax`}
                pct={t.neto_entretur > 0 ? (a.neto_entretur / t.neto_entretur) * 100 : 0}
                color="#8b5cf6" />
            ))}
            {venta.por_agencia.length > 6 && (
              <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 0.5 }}>
                +{venta.por_agencia.length - 6} agencias más
              </Typography>
            )}
          </Paper>
        </Grid>

        {venta.por_paquete.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 1 }}>POR PAQUETE</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellHead}>Paquete</TableCell>
                      <TableCell sx={cellHead} align="right">Reservas</TableCell>
                      <TableCell sx={cellHead} align="right">Pax</TableCell>
                      <TableCell sx={cellHead} align="right">Cliente pagó</TableCell>
                      <TableCell sx={cellHead} align="right">Neto Entretur</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {venta.por_paquete.map(p => (
                      <TableRow key={p.paquete_id}>
                        <TableCell sx={cell}>{p.nombre}</TableCell>
                        <TableCell sx={cell} align="right">{p.num_reservas}</TableCell>
                        <TableCell sx={cell} align="right">{p.pax}</TableCell>
                        <TableCell sx={cell} align="right">{fmtMoney(p.precio_cliente)}</TableCell>
                        <TableCell sx={cellStrong} align="right">{fmtMoney(p.neto_entretur)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

// ============================================================================
// Bloque 2 · RECAUDO
// ============================================================================
function BloqueRecaudo({ recaudo }) {
  return (
    <Box>
      <SectionHeader num="2" title="Recaudo" color="#16a34a"
        subtitle={`${recaudo.num_movimientos} movimiento(s) — fecha basada en creación del pago`} />

      {recaudo.num_movimientos === 0 ? (
        <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 13 }}>
          Sin movimientos en el rango.
        </Paper>
      ) : (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
              <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 1 }}>POR CUENTA DESTINO</Typography>
              {recaudo.por_cuenta.map((c, i) => (
                <BarRow key={i}
                  label={c.cuenta_nombre}
                  sublabel={c.tipo || (c.cuenta_id === null ? 'plata que entró pero al pago no se le asignó cuenta' : null)}
                  value={fmtMoney(c.monto)} valueRight={`${c.num} mov.`}
                  pct={recaudo.total > 0 ? (c.monto / recaudo.total) * 100 : 0}
                  color={c.cuenta_id === null ? '#f59e0b' : '#16a34a'} />
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
              <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 1 }}>POR ORIGEN</Typography>
              {recaudo.por_origen.map(o => (
                <BarRow key={o.origen}
                  label={o.label}
                  value={fmtMoney(o.monto)} valueRight={`${o.num} mov.`}
                  pct={recaudo.total > 0 ? (o.monto / recaudo.total) * 100 : 0}
                  color="#0d9488" />
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 1 }}>
                DETALLE ({recaudo.detalle.length})
              </Typography>
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellHead}>Fecha</TableCell>
                      <TableCell sx={cellHead}>Reserva</TableCell>
                      <TableCell sx={cellHead}>Cliente</TableCell>
                      <TableCell sx={cellHead}>Agencia</TableCell>
                      <TableCell sx={cellHead}>Origen</TableCell>
                      <TableCell sx={cellHead}>Cuenta</TableCell>
                      <TableCell sx={cellHead} align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recaudo.detalle.map(p => (
                      <TableRow key={p.pago_id} hover>
                        <TableCell sx={cellSmall}>{fmtDT(p.fecha)}</TableCell>
                        <TableCell sx={cellSmall}>
                          <span style={{ fontFamily: 'monospace', color: '#0891b2' }}>{p.codigo_reserva || '—'}</span>
                        </TableCell>
                        <TableCell sx={cellSmall}>{p.nombre_cliente || '—'}</TableCell>
                        <TableCell sx={cellSmall}>{p.agencia_nombre || <span style={{ color: '#cbd5e1' }}>—</span>}</TableCell>
                        <TableCell sx={cellSmall}>
                          <Chip size="small" label={p.origen_label}
                            sx={{ height: 20, fontSize: 10, bgcolor: '#f1f5f9' }} />
                        </TableCell>
                        <TableCell sx={cellSmall}>
                          {p.cuenta_nombre || <span style={{ color: '#cbd5e1', fontSize: 11 }}>sin cuenta</span>}
                        </TableCell>
                        <TableCell sx={cellStrong} align="right">{fmtMoney(p.monto)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ============================================================================
// Bloque 3 · CxC
// ============================================================================
function BloqueCxC({ cxc }) {
  return (
    <Box>
      <SectionHeader num="3" title="CxC generadas" color="#dc2626"
        subtitle="Reservas del rango con saldo a cargo de la agencia (pendiente de cobrar)" />

      {cxc.num_reservas === 0 ? (
        <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 13 }}>
          Sin CxC generadas en el rango.
        </Paper>
      ) : (
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
              <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 1 }}>POR AGENCIA</Typography>
              {cxc.por_agencia.map(a => (
                <BarRow key={a.agencia_id}
                  label={a.agencia_nombre}
                  value={fmtMoney(a.monto)} valueRight={`${a.num_reservas} res.`}
                  pct={cxc.total > 0 ? (a.monto / cxc.total) * 100 : 0}
                  color="#dc2626" />
              ))}
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
              <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 1 }}>
                DETALLE ({cxc.detalle.length})
              </Typography>
              <TableContainer sx={{ maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellHead}>Reserva</TableCell>
                      <TableCell sx={cellHead}>Cliente</TableCell>
                      <TableCell sx={cellHead}>Agencia</TableCell>
                      <TableCell sx={cellHead}>Estado</TableCell>
                      <TableCell sx={cellHead} align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cxc.detalle.map(r => (
                      <TableRow key={r.reservacion_id} hover>
                        <TableCell sx={cellSmall}>
                          <span style={{ fontFamily: 'monospace', color: '#0891b2' }}>{r.codigo_reserva}</span>
                        </TableCell>
                        <TableCell sx={cellSmall}>{r.nombre_cliente}</TableCell>
                        <TableCell sx={cellSmall}>{r.agencia_nombre || '—'}</TableCell>
                        <TableCell sx={cellSmall}>
                          <Chip size="small" label={r.estado} sx={{ height: 20, fontSize: 10, bgcolor: '#f1f5f9' }} />
                        </TableCell>
                        <TableCell sx={cellStrong} align="right">{fmtMoney(r.monto)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

// ============================================================================
// Bloque 4 · COMISIONES
// ============================================================================
function BloqueComisiones({ comisiones }) {
  const c = comisiones;
  return (
    <Box>
      <SectionHeader num="4" title="Comisiones a agencias" color="#7c3aed"
        subtitle="Generadas / anticipadas / pagadas del rango + pendiente vivo global" />

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <MetricSmall label="Generadas (rango)" value={c.generadas} color="#7c3aed" />
        <MetricSmall label="Anticipadas (rango)" value={c.anticipadas} color="#0ea5e9" />
        <MetricSmall label="Descuentos comisión" value={c.descuentos} color="#f59e0b" />
        <MetricSmall label="Pagadas (rango)" value={c.pagadas} color="#16a34a" />
        <MetricSmall label="PENDIENTE GLOBAL" value={c.pendientes_global} color="#dc2626" primary
          tooltip="Saldo vivo hoy — no solo del rango. Es lo que Entretur aún debe a las agencias." />
      </Grid>

      <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, mb: 1 }}>POR AGENCIA</Typography>
        {c.por_agencia.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Sin movimientos de comisión.</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={cellHead}>Agencia</TableCell>
                  <TableCell sx={cellHead} align="right">Generada rango</TableCell>
                  <TableCell sx={cellHead} align="right">Anticipada rango</TableCell>
                  <TableCell sx={cellHead} align="right">Pagada rango</TableCell>
                  <TableCell sx={cellHead} align="right">Pendiente hoy</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {c.por_agencia.map(a => (
                  <TableRow key={a.agencia_id} hover>
                    <TableCell sx={cell}>{a.agencia_nombre}</TableCell>
                    <TableCell sx={cell} align="right">{fmtMoney(a.generada)}</TableCell>
                    <TableCell sx={cell} align="right">{fmtMoney(a.anticipada)}</TableCell>
                    <TableCell sx={cell} align="right" style={{ color: a.pagada > 0 ? '#16a34a' : undefined }}>
                      {fmtMoney(a.pagada)}
                    </TableCell>
                    <TableCell sx={cellStrong} align="right" style={{ color: a.pendiente > 0 ? '#dc2626' : '#94a3b8' }}>
                      {fmtMoney(a.pendiente)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

// ============================================================================
// Bloque 5 · CAJA
// ============================================================================
function BloqueCaja({ caja }) {
  return (
    <Box>
      <SectionHeader num="5" title="Resumen de caja" color="#0d9488"
        subtitle="Recaudo menos comisiones pagadas en el rango" />
      <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0',
          display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <MetricInline label="Recaudo" value={fmtMoney(caja.recaudo)} color="#16a34a" op="" />
        <MetricInline label="Comisiones pagadas" value={fmtMoney(caja.comisiones_pagadas)} color="#7c3aed" op="−" />
        <Box sx={{ flex: 1 }} />
        <Box>
          <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
            Neto del rango
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: caja.neto >= 0 ? '#0d9488' : '#dc2626' }}>
            {fmtMoney(caja.neto)}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

// ============================================================================
// Sub-componentes
// ============================================================================
function Row({ label, value, muted, bold }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
      <Typography sx={{ fontSize: 12.5, color: muted ? '#94a3b8' : '#334155' }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: bold ? 800 : 500,
        color: bold ? '#0f172a' : muted ? '#94a3b8' : '#0f172a' }}>
        {value}
      </Typography>
    </Box>
  );
}

function BarRow({ label, sublabel, value, valueRight, pct, color }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 0.25 }}>
        <Box sx={{ minWidth: 0, flex: 1, mr: 1 }}>
          <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a' }}>{label}</Typography>
          {sublabel && (
            <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>{sublabel}</Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>{value}</Typography>
          {valueRight && (
            <Typography sx={{ fontSize: 10.5, color: '#94a3b8' }}>{valueRight}</Typography>
          )}
        </Box>
      </Box>
      <LinearProgress variant="determinate" value={Math.min(pct, 100)}
        sx={{ height: 4, borderRadius: 2, bgcolor: `${color}20`,
          '& .MuiLinearProgress-bar': { bgcolor: color } }} />
    </Box>
  );
}

function MetricSmall({ label, value, color, primary, tooltip }) {
  return (
    <Grid item xs={6} md>
      <Tooltip title={tooltip || ''} arrow disableHoverListener={!tooltip}>
        <Paper sx={{
          p: 1.25, borderRadius: 2,
          bgcolor: primary ? color : '#fff',
          color: primary ? '#fff' : '#0f172a',
          border: primary ? 'none' : `1px solid ${color}30`,
          borderLeft: primary ? 'none' : `3px solid ${color}`,
          cursor: tooltip ? 'help' : 'default',
        }}>
          <Typography sx={{
            fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
            color: primary ? '#f5f3ff' : '#64748b', textTransform: 'uppercase',
          }}>
            {label}
          </Typography>
          <Typography sx={{
            fontSize: primary ? 18 : 16, fontWeight: 800, mt: 0.25,
            color: primary ? '#fff' : color,
          }}>
            {fmtMoney(value)}
          </Typography>
        </Paper>
      </Tooltip>
    </Grid>
  );
}

function MetricInline({ label, value, color, op }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {op && <Typography sx={{ fontSize: 24, color: '#94a3b8', fontWeight: 700 }}>{op}</Typography>}
      <Box>
        <Typography sx={{ fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>{label}</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color }}>{value}</Typography>
      </Box>
    </Box>
  );
}

const cellHead = { fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', bgcolor: '#f8fafc', py: 0.75 };
const cell = { fontSize: 12.5, color: '#334155', py: 0.75 };
const cellSmall = { fontSize: 11.5, color: '#334155', py: 0.5 };
const cellStrong = { fontSize: 12.5, fontWeight: 700, color: '#0f172a', py: 0.75 };
